import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { useWallet } from 'use-wallet';
import Button from 'react-bootstrap/Button';
import './index.scss';
import { useOnchainAccountLendingPool } from '../../hooks/useOnchain';
import AccountLendingPoolLPRow from './AccountLendingPoolLPRow';
import PoolTokenContext from '../../contexts/PoolToken';
import AccountLendingPoolPageSelector from './AccountLendingPoolPageSelector';
import AccountLendingPoolSupplyRow from './AccountLendingPoolSupplyRow';
import AccountLendingPoolBorrowRow from './AccountLendingPoolBorrowRow';
import AccountLendingPoolDetailsLeverage from './AccountLendingPoolDetailsLeverage';
import AccountLendingPoolDetailsEarnInterest from './AccountLendingPoolDetailsEarnInterest';
import useGenericCall from '../../hooks/useGenericCall';
import AccountLendingPoolFarming from './AccountLendingPoolFarming';
import { PoolTokenType } from 'impermax-sdk';
import usePairAddress from '../../hooks/usePairAddress';

export enum AccountLendingPoolPage {
  UNINITIALIZED,
  LEVERAGE,
  EARN_INTEREST,
  FARMING,
}

interface AccountLendingPoolContainerProps {
  children: any;
}

function AccountLendingPoolContainer({ children }: AccountLendingPoolContainerProps) {
  return (<div className="account-lending-pool">
    <Card>
      <Card.Body>
        { children }
      </Card.Body>
    </Card>
  </div>);
}

/**
 * Generate the Account Lending Pool card, giving details about the particular user's equity in the pool.
 * @params AccountLendingPoolProps
 */
export default function AccountLendingPool() {
  const { connect, account } = useWallet();
  const data = useGenericCall(useOnchainAccountLendingPool(), async  onchainAccountLendingPool => ({
    collateralUSD: await onchainAccountLendingPool.getCollateral().getDepositedUSD(),
    suppliedUSD: await onchainAccountLendingPool.getSuppliedUSD(),
  }));
  const [pageSelected, setPageSelected] = useState<AccountLendingPoolPage>(AccountLendingPoolPage.UNINITIALIZED);

  if (!data) return (null);

  const actualPageSelected = pageSelected === AccountLendingPoolPage.UNINITIALIZED 
    ? data.collateralUSD > 0 || !data.suppliedUSD
      ? AccountLendingPoolPage.LEVERAGE
      : AccountLendingPoolPage.EARN_INTEREST
    : pageSelected;

  if (!account) return (
    <AccountLendingPoolContainer>
      <div className="text-center py-5">
        <Button onClick={() => {connect('injected')}} className="button-green">Connect to use the App</Button>
      </div>
    </AccountLendingPoolContainer>
  );
    
  return (
    <AccountLendingPoolContainer>
      <AccountLendingPoolPageSelector pageSelected={actualPageSelected} setPageSelected={setPageSelected} />
      { actualPageSelected === AccountLendingPoolPage.LEVERAGE && (<>
        <AccountLendingPoolDetailsLeverage />
        <PoolTokenContext.Provider value={PoolTokenType.Collateral}>
          <AccountLendingPoolLPRow />
        </PoolTokenContext.Provider>
        <PoolTokenContext.Provider value={PoolTokenType.BorrowableA}>
          <AccountLendingPoolBorrowRow />
        </PoolTokenContext.Provider>
        <PoolTokenContext.Provider value={PoolTokenType.BorrowableB}>
          <AccountLendingPoolBorrowRow />
        </PoolTokenContext.Provider>
      </>) }
      { actualPageSelected === AccountLendingPoolPage.EARN_INTEREST && (<>
        <AccountLendingPoolDetailsEarnInterest />
        <PoolTokenContext.Provider value={PoolTokenType.BorrowableA}>
          <AccountLendingPoolSupplyRow />
        </PoolTokenContext.Provider>
        <PoolTokenContext.Provider value={PoolTokenType.BorrowableB}>
          <AccountLendingPoolSupplyRow />
        </PoolTokenContext.Provider>
      </>) }
      { actualPageSelected === AccountLendingPoolPage.FARMING && (<>
        <AccountLendingPoolFarming />
      </>) }
    </AccountLendingPoolContainer>
  );
}